// tenant/SignupPages/BillingInfoPage.tsx
import React, { CSSProperties, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import TenantSignupLayout from '../TenantSignupLayout';
import { Link } from 'react-router-dom';

import { Auth, API, graphqlOperation } from 'aws-amplify';
import * as queries from '../../graphql/queries';
import { createOrder, updateTenant } from '../../graphql/mutations';
import { createAppointment } from '../../components/TenantApp/createAppointment';
import { submitStripeBillingViaSetupIntent } from '../../components/TenantApp/createStripeCustomer';
import type { StripeBillingInput } from '../../components/TenantApp/createStripeCustomer';
import { enqueuePmsJob } from '../../integrations/pms';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

// Set to true once Stripe is live
const STRIPE_ENABLED = false;

// ---- shared state types ----
type BaseSignupState = {
  facilityID: string;
  name: string;
  email: string;
  password: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  size: string;
  parking: string;
  building: string;
  minStay: string;
  typeOfPlan: string;
  price: string;
  referralCode?: string;
};

type Address = {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  specialInstructions: string;
  parking: string;
  building: string;
};

type LocationState = BaseSignupState & {
  dateSelected: string;
  timeSelected: string;
  address: Address;
  selected?: string[];
  needHelp?: string;
  needPack?: string;
};

// ---- styles ----
const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#222',
  fontSize: 14,
};

const labelStyle: CSSProperties = {
  ...baseFont,
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #CCCCCC',
  fontSize: 14,
  fontFamily: baseFont.fontFamily,
  boxSizing: 'border-box' as const,
};

const errorTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#d32f2f',
  marginTop: 8,
};

const hintTextStyle: CSSProperties = {
  ...baseFont,
  fontSize: 12,
  color: '#777',
  marginTop: 4,
};

const sectionCard: CSSProperties = {
  backgroundColor: '#3A1F52',
  borderRadius: 12,
  padding: 16,
  color: '#fff',
  marginBottom: 20,
};

const summaryRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 8,
};

const primaryButton: CSSProperties = {
  ...baseFont,
  marginTop: 24,
  width: '100%',
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'center' as const,
};

const disabledButton: CSSProperties = {
  ...primaryButton,
  opacity: 0.6,
  cursor: 'not-allowed',
};

const cardElementContainer: CSSProperties = {
  width: '100%',
  padding: '10px',
  borderRadius: 6,
  border: '1px solid #4826A0',
  fontSize: 14,
  backgroundColor: 'rgba(10, 4, 18, 0.9)',
  boxSizing: 'border-box' as const,
  marginTop: 4,
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '14px',
      color: '#F5F5FA',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      '::placeholder': { color: '#8B7AA8' },
      iconColor: '#9B6DE0',
    },
    invalid: {
      color: '#FFB3B3',
      iconColor: '#FFB3B3',
    },
  },
};

// ---- inner form (must live inside <Elements>) ----

interface BillingFormInnerProps {
  state: LocationState;
}

const BillingFormInner: React.FC<BillingFormInnerProps> = ({ state }) => {
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();

  const [billingName, setBillingName] = useState('');
  const [billingAddressLine1, setBillingAddressLine1] = useState('');
  const [billingAddressLine2, setBillingAddressLine2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');

  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const [loadingPrefill, setLoadingPrefill] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const prefill = async () => {
      try {
        const currentUserInfo = await Auth.currentUserInfo();
        const userName = currentUserInfo?.attributes?.name;

        let tenantAddress = state.address;

        try {
          const tenantRes: any = await API.graphql({
            query: queries.getTenant,
            variables: { id: state.email },
          });
          const tenant = tenantRes?.data?.getTenant;
          if (tenant?.address) {
            tenantAddress = {
              addressLine1: tenant.address.addressLine1,
              addressLine2: tenant.address.addressLine2 || '',
              city: tenant.address.city,
              state: tenant.address.state,
              zip: tenant.address.zip,
              specialInstructions: tenant.address.specialInstructions || '',
              parking: tenant.address.parking || '',
              building: tenant.address.building || '',
            };
          }
        } catch (err) {
          console.log('[BillingInfoPage] getTenant failed, falling back to state.address:', err);
        }

        setBillingName(userName || state.name);
        setBillingAddressLine1(tenantAddress.addressLine1);
        setBillingAddressLine2(tenantAddress.addressLine2);
        setBillingCity(tenantAddress.city);
        setBillingState(tenantAddress.state);
        setBillingZip(tenantAddress.zip);
      } catch (err) {
        console.log('[BillingInfoPage] prefill error:', err);
        setBillingName(state.name);
        setBillingAddressLine1(state.address.addressLine1);
        setBillingAddressLine2(state.address.addressLine2);
        setBillingCity(state.address.city);
        setBillingState(state.address.state);
        setBillingZip(state.address.zip);
      } finally {
        setLoadingPrefill(false);
      }
    };

    prefill();
  }, [state]);

  const validateAddressFields = (): boolean => {
    const errors: Record<string, string> = {};
    if (!billingAddressLine1.trim()) errors.billingAddressLine1 = 'Billing address is required.';
    if (!billingCity.trim()) errors.billingCity = 'City is required.';
    if (!billingState.trim()) errors.billingState = 'State is required.';
    if (!/^\d{5}$/.test(billingZip)) errors.billingZip = 'ZIP must be 5 digits.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('Please fix the highlighted fields.');
      return false;
    }
    setFormError(null);
    return true;
  };

  const formatAddress = (addr: Address): string => {
    const line2 = addr.addressLine2 ? ` ${addr.addressLine2}` : '';
    return `${addr.addressLine1}${line2}, ${addr.city}, ${addr.state} ${addr.zip}`;
  };

  const handleSubmit = async () => {
    if (STRIPE_ENABLED && !validateAddressFields()) return;

    setSubmitting(true);
    setFormError(null);

    try {
      if (STRIPE_ENABLED) {
        if (!stripe || !elements) {
          setFormError('Stripe has not loaded yet. Please try again.');
          setSubmitting(false);
          return;
        }

        const billingData: StripeBillingInput = {
          email: state.email,
          billingName,
          billingAddressLine1,
          billingAddressLine2,
          billingCity,
          billingState,
          billingZip,
        };

        const { customerId, paymentMethodId } = await submitStripeBillingViaSetupIntent(
          stripe,
          elements,
          billingData
        );

        await API.graphql(
          graphqlOperation(updateTenant, {
            input: {
              id: state.email,
              stripeCustomerID: customerId,
              card: {
                id: paymentMethodId,
                nickname: 'Card on file',
              },
            },
          })
        );
      }

      const orderId = uuidv4();
      const appointmentId = uuidv4();

      const slotPaid = !!(state as any).slotPaid;
      const paidPricingLabel = (state as any).paidPricingLabel as string | undefined;

      // Valet tier ('Basic' | 'Itemized') is stored in the order's moverOption
      // field — unused on signup pickups, and the mover app already reads Orders.
      const tier = ((state as any).tier as string | undefined) || 'Basic';

      const orderInput = {
        id: orderId,
        address: state.address,
        date: state.dateSelected,
        facilityID: state.facilityID,
        tenantID: state.email,
        jobType: 'PICKUP',
        status: 'PENDING',
        time: state.timeSelected,
        needHelp: state.needHelp ?? '',
        needPack: state.needPack ?? '',
        moverOption: tier,
        orderItems: state.selected ?? [],
        driver: 'None',
        isInitialOrder: true,
        chosenSlotPaid: slotPaid,
        chosenSlotPrice: slotPaid ? (paidPricingLabel ?? null) : 'Free',
      };

      const appointmentData = {
        id: appointmentId,
        orderID: orderId,
        driver: '',
        facilityID: state.facilityID,
        date: state.dateSelected,
        time: state.timeSelected,
        type: 'Order',
        tenantID: state.email,
      };

      await API.graphql(graphqlOperation(createOrder, { input: orderInput }));
      await createAppointment(appointmentData);

      // Mirror the tenant into the facility's storage software. Best-effort:
      // the customer must reach the confirmation page regardless, and nothing
      // is sent to the vendor while the integration is in shadow mode.
      try {
        await enqueuePmsJob({
          facilityID: state.facilityID,
          op: 'ensureTenant',
          binsID: state.email,
          payload: {
            binsTenantID: state.email,
            name: state.name,
            email: state.email,
            phone: state.phone,
            address: {
              line1: state.address.addressLine1,
              line2: state.address.addressLine2,
              city: state.address.city,
              state: state.address.state,
              zip: state.address.zip,
            },
          },
        });
      } catch (pmsError) {
        console.warn('[pms] ensureTenant enqueue failed:', pmsError);
      }

      history.push('/signup/initial-confirmation', {
        dateSelected: state.dateSelected,
        timeSelected: state.timeSelected,
        address: formatAddress(state.address),
        type: 'PICKUP',
      });
    } catch (err: any) {
      console.error('[BillingInfoPage] submit error:', err);
      setFormError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isValidInput = STRIPE_ENABLED
    ? (!loadingPrefill &&
        cardComplete &&
        billingAddressLine1.trim().length > 0 &&
        billingCity.trim().length > 0 &&
        billingState.trim().length > 0 &&
        billingZip.trim().length === 5)
    : true;

  const addressSummary = formatAddress(state.address);
  const selectedItemsSummary =
    state.selected && state.selected.length
      ? state.selected.join(', ')
      : 'No large items specified';

  return (
    <div style={{ ...baseFont }}>
      {/* Summary card */}
      <div style={sectionCard}>
        <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
          Review your pickup
        </h3>
        <div style={summaryRow}>
          <span style={{ color: '#D0C9E9' }}>Date</span>
          <span>{moment(state.dateSelected).format('MMMM DD, YYYY')}</span>
        </div>
        <div style={summaryRow}>
          <span style={{ color: '#D0C9E9' }}>Time</span>
          <span>{state.timeSelected}</span>
        </div>
        <div style={summaryRow}>
          <span style={{ color: '#D0C9E9' }}>Address</span>
          <span style={{ textAlign: 'right' }}>{addressSummary}</span>
        </div>
        <div style={summaryRow}>
          <span style={{ color: '#D0C9E9' }}>Estimated Size</span>
          <span>{state.size}</span>
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#D0C9E9', marginBottom: 4 }}>Large items</div>
          <div style={{ fontSize: 13 }}>{selectedItemsSummary}</div>
        </div>
      </div>

      {/* Card + billing address */}
      <div style={sectionCard}>
        {loadingPrefill ? (
          <p>Loading your billing details…</p>
        ) : (
          <>
            {STRIPE_ENABLED && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
                  Credit card
                </h3>
                <p style={{ ...hintTextStyle, color: '#D0C9E9' }}>
                  This card is used to secure your reservation. You won't be charged until your items
                  are stored.
                </p>
                <div style={{ marginTop: 12 }}>
                  <label style={{ ...labelStyle, color: '#fff' }}>Card details</label>
                  <div style={cardElementContainer}>
                    <CardElement
                      options={CARD_ELEMENT_OPTIONS}
                      onChange={e => {
                        setCardComplete(e.complete);
                        setCardError(e.error ? e.error.message : null);
                      }}
                    />
                  </div>
                  {cardError && <div style={{ ...errorTextStyle, color: '#FFB3B3' }}>{cardError}</div>}
                </div>
              </>
            )}

            {STRIPE_ENABLED && (
              <>
                <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
                  Billing address
                </h3>

                <div style={{ marginTop: 8 }}>
                  <label style={{ ...labelStyle, color: '#fff' }}>Name on card</label>
                  <input
                    type="text"
                    value={billingName}
                    onChange={e => setBillingName(e.target.value)}
                    style={inputStyle}
                    disabled={submitting}
                  />
                </div>

                <div style={{ marginTop: 8 }}>
                  <label style={{ ...labelStyle, color: '#fff' }}>Address Line 1</label>
                  <input
                    type="text"
                    value={billingAddressLine1}
                    onChange={e => {
                      setBillingAddressLine1(e.target.value);
                      setFieldErrors(prev => ({ ...prev, billingAddressLine1: '' }));
                    }}
                    style={inputStyle}
                    disabled={submitting}
                  />
                  {fieldErrors.billingAddressLine1 && (
                    <div style={errorTextStyle}>{fieldErrors.billingAddressLine1}</div>
                  )}
                </div>

                <div style={{ marginTop: 8 }}>
                  <label style={{ ...labelStyle, color: '#fff' }}>
                    Address Line 2 <span style={{ fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={billingAddressLine2}
                    onChange={e => setBillingAddressLine2(e.target.value)}
                    style={inputStyle}
                    disabled={submitting}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <label style={{ ...labelStyle, color: '#fff' }}>City</label>
                    <input
                      type="text"
                      value={billingCity}
                      onChange={e => {
                        setBillingCity(e.target.value);
                        setFieldErrors(prev => ({ ...prev, billingCity: '' }));
                      }}
                      style={inputStyle}
                      disabled={submitting}
                    />
                    {fieldErrors.billingCity && (
                      <div style={errorTextStyle}>{fieldErrors.billingCity}</div>
                    )}
                  </div>
                  <div style={{ width: 80, minWidth: 80 }}>
                    <label style={{ ...labelStyle, color: '#fff' }}>State</label>
                    <input
                      type="text"
                      value={billingState}
                      onChange={e => {
                        setBillingState(e.target.value.toUpperCase());
                        setFieldErrors(prev => ({ ...prev, billingState: '' }));
                      }}
                      style={inputStyle}
                      maxLength={2}
                      disabled={submitting}
                    />
                    {fieldErrors.billingState && (
                      <div style={errorTextStyle}>{fieldErrors.billingState}</div>
                    )}
                  </div>
                  <div style={{ width: 100, minWidth: 90 }}>
                    <label style={{ ...labelStyle, color: '#fff' }}>ZIP</label>
                    <input
                      type="text"
                      value={billingZip}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setBillingZip(v);
                        setFieldErrors(prev => ({ ...prev, billingZip: '' }));
                      }}
                      style={inputStyle}
                      disabled={submitting}
                    />
                    {fieldErrors.billingZip && (
                      <div style={errorTextStyle}>{fieldErrors.billingZip}</div>
                    )}
                  </div>
                </div>

                <p style={{ ...hintTextStyle, color: '#D0C9E9', marginTop: 16 }}>
                  By pressing "Request pick up", you agree to Bins Storage's{' '}
                  <Link to="/signup/terms" style={{ color: '#D0C9E9', textDecoration: 'underline' }}>
                    Terms & Conditions
                  </Link>
                  ,{' '}
                  <Link to="/signup/privacy" style={{ color: '#D0C9E9', textDecoration: 'underline' }}>
                    Privacy Policy
                  </Link>
                  , and{' '}
                  <Link to="/signup/lease" style={{ color: '#D0C9E9', textDecoration: 'underline' }}>
                    Lease Agreement
                  </Link>
                  .
                </p>
              </>
            )}
          </>
        )}
      </div>

      {formError && <div style={errorTextStyle}>{formError}</div>}

      <button
        type="button"
        onClick={handleSubmit}
        style={!isValidInput || submitting ? disabledButton : primaryButton}
        disabled={!isValidInput || submitting}
      >
        {submitting ? 'Requesting pick up…' : 'Request pick up'}
      </button>
    </div>
  );
};

// ---- outer component: provides <Elements> context ----

const BillingInfoPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const state = location.state;

  if (!state) {
    return (
      <TenantSignupLayout
        title="Billing information"
        subtitle="We need your signup details before you can finish."
        step={7}
        totalSteps={7}
      >
        <div style={{ ...baseFont }}>
          <p style={{ marginBottom: 16 }}>
            It looks like you opened this page directly. Please go back to the beginning of signup.
          </p>
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={primaryButton}
          >
            Go to signup
          </button>
        </div>
      </TenantSignupLayout>
    );
  }

  return (
    <TenantSignupLayout
      title="Billing information"
      subtitle="You won't be charged until your items are stored."
      step={7}
      totalSteps={7}
    >
      <Elements stripe={stripePromise}>
        <BillingFormInner state={state} />
      </Elements>
    </TenantSignupLayout>
  );
};

export default BillingInfoPage;
